/**
 * How a question's picture choices are shown to the shopper — one contract for
 * the storefront widget, Studio's question editor, the API's config check and
 * the authoring agent.
 *
 * The rules, in order of who wins:
 *
 *  1. The question's own setting (Studio → option set → question), when set.
 *  2. The shop's setting (Studio → Settings → Storefront look → Swatches):
 *     the tile size and the two backdrop colours.
 *  3. The built-in default below.
 *
 * Size. Every picture choice of a question is the same size, whatever its
 * caption. The size is the shop's tile size scaled by the question's
 * `pictureSize`. A caption being left off never makes a tile smaller — that
 * coupling is what made "Background 1 … 7" tiny beside a titled question.
 *
 * Captions. `auto` leaves off captions that only count the choices ("Pin 1" …
 * "Pin 7", "Background 1" … "Background 7"); the name stays on the button for
 * screen readers. `show` and `hide` override that for the whole question. A
 * lead every shown caption repeats ("You are my Missing Piece - ") is dropped,
 * so the tile shows the part that tells the choices apart.
 *
 * Backdrop. A picture sits on a backdrop it can be seen on. `auto` reads the
 * picture's own pixels: dark or coloured art gets the light backdrop, mostly
 * white art (a white pin, a white outline, white lettering) gets the dark one.
 * A choice's colour (`bgColor` — Customily's "Swatch Color") is the colour the
 * choice STANDS FOR, the title's ink or the skin tone; it is never painted
 * behind the picture, where it hides art of that very colour.
 *
 * Fit. `auto` fills the tile with an opaque picture (a photo, a style preview)
 * and shows a transparent one whole, with its empty transparent margins cut
 * off, so a line of lettering in a square thumbnail reads at tile width.
 * `whole` shows every picture exactly as uploaded.
 */

export const CHOICE_PICTURE_SIZES = ['small', 'medium', 'large'] as const;
export const CHOICE_CAPTIONS = ['auto', 'show', 'hide'] as const;
export const CHOICE_BACKDROPS = ['auto', 'light', 'dark'] as const;
export const CHOICE_FITS = ['auto', 'whole'] as const;

export type ChoicePictureSize = (typeof CHOICE_PICTURE_SIZES)[number];
export type ChoiceCaptions = (typeof CHOICE_CAPTIONS)[number];
export type ChoiceBackdrop = (typeof CHOICE_BACKDROPS)[number];
export type ChoiceFit = (typeof CHOICE_FITS)[number];

export interface ChoiceDisplay {
  pictureSize: ChoicePictureSize;
  captions: ChoiceCaptions;
  pictureBackdrop: ChoiceBackdrop;
  pictureFit: ChoiceFit;
}

/** The shop's tile size when it has none of its own, in CSS pixels. */
export const CHOICE_TILE_DEFAULT_PX = 80;
export const CHOICE_TILE_MIN_PX = 48;
export const CHOICE_TILE_MAX_PX = 200;
/** Backdrops when the shop set none: the widget's light surface and a mid grey. */
export const CHOICE_BACKDROP_LIGHT = '#f6f6f8';
export const CHOICE_BACKDROP_DARK = '#858b96';

/** Each size as a share of the shop's tile size. */
export const CHOICE_SIZE_SCALE: Record<ChoicePictureSize, number> = { small: 0.75, medium: 1, large: 1.4 };

/** The question-config keys this contract owns, and each one's allowed values. */
const KEYS = {
  pictureSize: CHOICE_PICTURE_SIZES,
  captions: CHOICE_CAPTIONS,
  pictureBackdrop: CHOICE_BACKDROPS,
  pictureFit: CHOICE_FITS,
} as const;

/**
 * A question's display settings with the defaults filled in. `compact` is a
 * dropdown shown as picture swatches, which has always been the small size.
 * A clipart multi-pick grid is compact too. An unknown value reads as the
 * default: a stale config never breaks a form.
 */
export function choiceDisplayOf(
  config: Record<string, unknown> | undefined,
  options: { compact?: boolean } = {},
): ChoiceDisplay {
  const pick = <T extends string>(key: keyof typeof KEYS, fallback: T): T => {
    const raw = config?.[key];
    return typeof raw === 'string' && (KEYS[key] as readonly string[]).includes(raw) ? (raw as T) : fallback;
  };
  return {
    pictureSize: pick<ChoicePictureSize>('pictureSize', options.compact ? 'small' : 'medium'),
    captions: pick<ChoiceCaptions>('captions', 'auto'),
    pictureBackdrop: pick<ChoiceBackdrop>('pictureBackdrop', 'auto'),
    pictureFit: pick<ChoiceFit>('pictureFit', 'auto'),
  };
}

/**
 * Refuses a display setting outside its list, so a typo does not silently
 * fall back to the default. Unset, null and '' are "use the default".
 */
export function choiceDisplayError(type: string, config: Record<string, unknown>): string | null {
  for (const [key, allowed] of Object.entries(KEYS)) {
    const value = config[key];
    if (value === undefined || value === null || value === '') continue;
    if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) {
      return `${type} config.${key} must be one of ${allowed.join(', ')}`;
    }
  }
  return null;
}

/**
 * Only the display keys of a config, with defaults and empties dropped — what
 * an authoring blueprint writes onto a question.
 */
export function choiceDisplayPatch(input: Partial<Record<keyof ChoiceDisplay, string>>): Partial<ChoiceDisplay> {
  const out: Record<string, string> = {};
  for (const [key, allowed] of Object.entries(KEYS)) {
    const value = input[key as keyof ChoiceDisplay];
    if (typeof value === 'string' && (allowed as readonly string[]).includes(value)) out[key] = value;
  }
  return out as Partial<ChoiceDisplay>;
}

/**
 * Labels that only count the choices — "Pin 1" … "Pin 7", "Stil 2" — say
 * nothing the picture does not, and under a small tile they cost a whole line
 * each. True when every label is blank or the same word with a small number.
 *
 * The word must be a word: "A4" / "A3" or "10" / "20" are sizes and amounts,
 * and those the shopper does need to read.
 */
export function labelsOnlyCount(labels: string[]): boolean {
  if (labels.length === 0) return false;
  let stem: string | undefined;
  for (const raw of labels) {
    const label = raw.trim();
    if (label === '') continue;
    const match = /^(\p{L}[\p{L}\s]*?)[\s#._-]*(\d{1,2})$/u.exec(label);
    if (!match || match[1]!.trim().length < 2) return false;
    const word = match[1]!.trim().toLowerCase();
    if (stem === undefined) stem = word;
    else if (word !== stem) return false;
  }
  return true;
}

/**
 * The captions a question shows, with a lead every caption repeats dropped:
 * "You are my Missing Piece - Black" … "- Gold" become "Black" … "Gold". The
 * lead must end at a separator (" - ", " – ", ": ", " / ", " | "), and each
 * caption must keep something of its own; otherwise the captions are kept
 * whole. A question with one choice keeps its name. The full name stays on
 * the button for screen readers and the tooltip.
 */
export function captionsOf(labels: string[]): string[] {
  const named = labels.map((l) => l.trim());
  if (named.length < 2 || named.some((l) => l === '')) return named;
  const separator = /\s+[-–—|\/]\s+|:\s+/gu;
  let lead: string | undefined;
  for (const match of named[0]!.matchAll(separator)) {
    const candidate = named[0]!.slice(0, match.index! + match[0].length);
    if (named.every((l) => l.startsWith(candidate) && l.length > candidate.length)) lead = candidate;
  }
  return lead ? named.map((l) => l.slice(lead!.length).trim()) : named;
}

/** Whether a question's captions are shown, by the rule above. */
export function showsCaptions(display: ChoiceDisplay, labels: string[]): boolean {
  if (display.captions === 'show') return true;
  if (display.captions === 'hide') return false;
  return !labelsOnlyCount(labels);
}

// ── Font choices ───────────────────────────────────────────────────────────

/**
 * A choice that picks a typeface is shown IN that typeface. This is the rule
 * for every font picker: the storefront form, Studio's question editor and any
 * picker added later.
 *
 * "Font 1" … "Font 5" (a text layer's font list turned into a question) or a
 * font set's family names say nothing about how the words will look, and the
 * shopper should not have to pick each one and study the preview to find out.
 * So each choice is drawn in its own font, and the storefront never offers
 * font choices as a native dropdown — a phone's dropdown ignores fonts. The
 * widget shows them as tiles with the shopper's own words in each font
 * (`fontChoiceSample`), whatever the question's type; a picture the merchant
 * put on a choice (a font specimen) is shown as that picture.
 *
 * A choice's font is its `meta.fontId` (a font-set fill writes it), else its
 * value when the question has a `font` binding — the value IS the font id the
 * binding writes onto the layer. A font the definition does not carry is not
 * previewed; that choice shows its plain name.
 */
export function choiceFontId(
  choice: { value: string; meta?: Record<string, unknown> | null },
  bindsFont: boolean,
  known: (fontId: string) => boolean,
): string | null {
  const own = choice.meta?.fontId;
  if (typeof own === 'string' && own !== '' && known(own)) return own;
  return bindsFont && choice.value !== '' && known(choice.value) ? choice.value : null;
}

/** The most characters a font sample shows before it is cut with "…". */
export const FONT_SAMPLE_MAX_CHARS = 24;

/**
 * The words a font choice is drawn with: the first line of what the layer will
 * print (the shopper's answer, else the layer's own default text), cut to
 * `FONT_SAMPLE_MAX_CHARS`; the choice's name while there is nothing to print.
 */
export function fontChoiceSample(text: string | null | undefined, name: string): string {
  const line =
    (text ?? '')
      .split(/\r?\n/)
      .map((l) => l.replace(/\s+/g, ' ').trim())
      .find((l) => l !== '') ?? '';
  if (line === '') return name;
  const chars = [...line];
  return chars.length > FONT_SAMPLE_MAX_CHARS ? `${chars.slice(0, FONT_SAMPLE_MAX_CHARS - 1).join('').trimEnd()}…` : line;
}

/** For `get_capabilities`: what an agent may set and what each setting does. */
export const CHOICE_DISPLAY_CAPABILITY = {
  applies: 'Picture choices: swatch questions whose choices carry pictures, dropdowns shown as picture swatches, and clipart multi-pick grids.',
  field: {
    display: {
      pictureSize: `${CHOICE_PICTURE_SIZES.join(' | ')} — scale of the shop tile size (${Object.entries(CHOICE_SIZE_SCALE).map(([k, v]) => `${k} ${v}×`).join(', ')}); default medium, small for a dropdown swatch and a clipart multi-pick grid`,
      captions: 'auto | show | hide — auto hides captions that only count the choices ("Pin 1" … "Pin 7"); shown captions drop a lead every one repeats ("Title - Black" … → "Black")',
      pictureBackdrop: 'auto | light | dark — auto reads the picture: mostly white art gets the dark backdrop, anything else the light one',
      pictureFit: 'auto | whole — auto fills the tile with opaque pictures and trims empty transparent margins; whole shows every picture as uploaded',
    },
  },
  rules: [
    'Every picture choice of a question is the same size; hiding captions never shrinks tiles.',
    'A choice colour (bgColor) is what the choice stands for and is never used as the picture backdrop.',
    'Question setting beats shop setting beats built-in default.',
    'Font choices are always shown in their own font: a question whose choices pick a font (a `font` binding with the font id as each value, or a value\'s meta.fontId) is drawn as tiles with the shopper\'s words (else the choice name) in each font, whether it is a dropdown or a swatch — never a plain dropdown. `captions` still applies (auto hides "Font 1" … "Font N"). Nothing to author; does not affect print.',
  ],
  shopDefaults: `Storefront settings choiceTileSize (${CHOICE_TILE_MIN_PX}–${CHOICE_TILE_MAX_PX} px, default ${CHOICE_TILE_DEFAULT_PX}), choiceBackdropLight (default ${CHOICE_BACKDROP_LIGHT}), choiceBackdropDark (default ${CHOICE_BACKDROP_DARK}). Shop settings are human-only.`,
  authoring: 'Blueprint fields accept an optional `display` with these keys; Studio edits the same question config. Does not affect print.',
} as const;
